import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    Grid,
    Alert,
    CircularProgress,
    Card,
    CardContent,
    InputAdornment,
    Fade,
    Container,
    useTheme,
    alpha,
    Switch,
    FormControlLabel,
    Tooltip,
    IconButton,
    Tabs,
    Tab,
    Chip,
    Avatar,
    MenuItem,
} from '@mui/material';
import {
    Save as SaveIcon,
    Storage,
    Email,
    Chat,
    Business,
    Visibility,
    VisibilityOff,
    CheckCircle,
    Warning,
    Info,
    Refresh,
} from '@mui/icons-material';
import { observer } from 'mobx-react-lite';
import { useStore } from '../store';
import api from '../config/api';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`settings-tabpanel-${index}`}
            aria-labelledby={`settings-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
        </div>
    );
}

const Settings: React.FC = observer(() => {
    const { settingsStore, authStore } = useStore();
    const theme = useTheme();
    const [tabValue, setTabValue] = useState(0);
    const [showPasswords, setShowPasswords] = useState({
        smtp: false,
        mattermost: false,
        crm: false,
        tilda: false,
    });

    const [formData, setFormData] = useState({
        // Database
        database_host: '',
        database_port: 5432,
        database_user: '',
        database_password: '',
        database_name: '',
        database_ssl_mode: 'disable',

        // SMTP
        smtp_host: '',
        smtp_port: 587,
        smtp_username: '',
        smtp_password: '',
        smtp_from: '',
        smtp_enabled: true,

        // Mattermost
        mattermost_url: '',
        mattermost_token: '',
        mattermost_channel: 'general',
        mattermost_enabled: true,

        // CRM
        bpm_url: '',
        bpm_login: '',
        bpm_password: '',
        crm_enabled: true,

        // Tilda
        url: '',
        project_id: '',
        public_key: '',
        secret_key: '',
        tilda_enabled: true,
    });

    const [testResults, setTestResults] = useState<{[key: string]: { status: 'success' | 'error' | 'testing' | null, message?: string }}>({
        database: { status: null },
        smtp: { status: null },
        mattermost: { status: null },
        crm: { status: null },
        tilda: { status: null },
    });

    useEffect(() => {
        settingsStore.fetchSettings();
    }, [settingsStore]);

    useEffect(() => {
        if (settingsStore.settings) {
            const settings = settingsStore.settings;
            setFormData({
                // Database
                database_host: settings.database.host || '',
                database_port: settings.database.port || 5432,
                database_user: settings.database.user || '',
                database_password: '', // Не показываем текущий пароль
                database_name: settings.database.name || '',
                database_ssl_mode: settings.database.ssl_mode || 'disable',

                // SMTP
                smtp_host: settings.smtp.host || '',
                smtp_port: settings.smtp.port || 587,
                smtp_username: settings.smtp.username || '',
                smtp_password: '', // Не показываем текущий пароль
                smtp_from: settings.smtp.from || '',
                smtp_enabled: true,

                // Mattermost
                mattermost_url: settings.mattermost.url || '',
                mattermost_token: '', // Не показываем текущий токен
                mattermost_channel: settings.mattermost.channel || 'general',
                mattermost_enabled: true,

                // CRM
                bpm_url: settings.crm.bpm_url || '',
                bpm_login: settings.crm.bpm_login || '',
                bpm_password: '', // Не показываем текущий пароль
                crm_enabled: true,

                // Tilda
                url: settings.tilda?.url || '',
                project_id: settings.tilda?.project_id || '',
                public_key: '', // Не показываем текущий публичный ключ
                secret_key: '', // Не показываем текущий секретный ключ
                tilda_enabled: true,
            });
        }
    }, [settingsStore.settings]);

    const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = field.includes('port') ? parseInt(event.target.value) || 0 :
            field.includes('enabled') ? event.target.checked :
                event.target.value;
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }));
    };

    const togglePasswordVisibility = (field: keyof typeof showPasswords) => {
        setShowPasswords(prev => ({
            ...prev,
            [field]: !prev[field],
        }));
    };

    const testConnection = async (service: string) => {
        setTestResults(prev => ({
            ...prev,
            [service]: { status: 'testing' }
        }));

        try {
            if (service === 'smtp') {
                // Тестируем SMTP соединение через API
                const response = await api.post('/api/test-email');
                setTestResults(prev => ({
                    ...prev,
                    [service]: {
                        status: 'success',
                        message: 'Email соединение успешно установлено'
                    }
                }));
            } else {
                // Имитация тестирования для других сервисов
                setTimeout(() => {
                    const success = Math.random() > 0.3;
                    setTestResults(prev => ({
                        ...prev,
                        [service]: {
                            status: success ? 'success' : 'error',
                            message: success ? 'Соединение успешно установлено' : 'Не удалось установить соединение'
                        }
                    }));
                }, 2000);
            }
        } catch (error) {
            setTestResults(prev => ({
                ...prev,
                [service]: {
                    status: 'error',
                    message: 'Не удалось установить соединение'
                }
            }));
        }
    };

    const handleSave = async () => {
        const settingsData = {
            database: {
                host: formData.database_host,
                port: formData.database_port,
                user: formData.database_user,
                password: formData.database_password,
                name: formData.database_name,
                ssl_mode: formData.database_ssl_mode,
                timezone: 'UTC',
            },
            smtp: {
                host: formData.smtp_host,
                port: formData.smtp_port,
                username: formData.smtp_username,
                password: formData.smtp_password,
                from: formData.smtp_from,
            },
            mattermost: {
                url: formData.mattermost_url,
                token: formData.mattermost_token,
                channel: formData.mattermost_channel,
            },
            crm: {
                bpm_url: formData.bpm_url,
                bpm_login: formData.bpm_login,
                bpm_password: formData.bpm_password,
            },
            tilda: {
                url:  formData.url,
                project_id: formData.project_id,
                public_key: formData.secret_key,
                secret_key: formData.secret_key,
            },
        };

        const success = await settingsStore.updateSettings(settingsData);
        if (success) {
            // Сбрасываем пароли после успешного сохранения
            setFormData(prev => ({
                ...prev,
                database_password: '',
                smtp_password: '',
                mattermost_token: '',
                bpm_password: '',
                public_key: '',
                secret_key: '',
            }));
        }
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    if (!authStore.isAdmin) {
        return (
            <Container maxWidth="xl">
                <Alert severity="error">
                    У вас нет прав для доступа к настройкам
                </Alert>
            </Container>
        );
    }

    if (settingsStore.loading) {
        return (
            <Container maxWidth="xl">
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="400px">
                    <CircularProgress size={60} thickness={4} />
                    <Typography variant="h6" color="textSecondary" sx={{ mt: 3 }}>
                        Загрузка настроек...
                    </Typography>
                </Box>
            </Container>
        );
    }

    const SettingCard = ({
                             title,
                             icon,
                             enabled,
                             onTest,
                             testStatus,
                             children
                         }: {
        title: string;
        icon: React.ReactNode;
        enabled?: boolean;
        onTest?: () => void;
        testStatus?: { status: 'success' | 'error' | 'testing' | null, message?: string };
        children: React.ReactNode;
    }) => (
        <Card
            sx={{
                height: '100%',
                position: 'relative',
                overflow: 'visible',
                border: '1px solid',
                borderColor: enabled === false ? 'error.main' : 'divider',
                borderRadius: 2,
                transition: 'all 0.3s ease',
                '&:hover': {
                    boxShadow: 4,
                },
            }}
        >
            <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                    <Box display="flex" alignItems="center" gap={2}>
                        <Avatar
                            sx={{
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                color: 'primary.main',
                                width: 48,
                                height: 48,
                            }}
                        >
                            {icon}
                        </Avatar>
                        <Box>
                            <Typography variant="h6" fontWeight={700}>
                                {title}
                            </Typography>
                            {enabled !== undefined && (
                                <Chip
                                    label={enabled ? 'Активно' : 'Отключено'}
                                    color={enabled ? 'success' : 'error'}
                                    size="small"
                                    sx={{ mt: 0.5 }}
                                />
                            )}
                        </Box>
                    </Box>
                    {onTest && (
                        <Tooltip title="Тестировать соединение">
                            <IconButton
                                onClick={onTest}
                                disabled={testStatus?.status === 'testing'}
                                color={testStatus?.status === 'success' ? 'success' :
                                    testStatus?.status === 'error' ? 'error' : 'default'}
                            >
                                {testStatus?.status === 'testing' ? (
                                    <CircularProgress size={20} />
                                ) : testStatus?.status === 'success' ? (
                                    <CheckCircle />
                                ) : testStatus?.status === 'error' ? (
                                    <Warning />
                                ) : (
                                    <Refresh />
                                )}
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>

                {testStatus?.message && (
                    <Alert
                        severity={testStatus.status === 'success' ? 'success' : 'error'}
                        sx={{ mb: 2 }}
                    >
                        {testStatus.message}
                    </Alert>
                )}

                {children}
            </CardContent>
        </Card>
    );

    return (
        <Container maxWidth="xl">
            <Box mb={4}>
                <Typography
                    variant="h3"
                    sx={{
                        fontWeight: 800,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        mb: 1
                    }}
                >
                    Настройки системы
                </Typography>
                <Typography variant="h6" color="textSecondary">
                    Конфигурация подключений и интеграций
                </Typography>
            </Box>

            {settingsStore.error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {settingsStore.error}
                </Alert>
            )}

            <Paper sx={{ mb: 3 }}>
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                        '& .MuiTab-root': {
                            minHeight: 64,
                            textTransform: 'none',
                            fontSize: '1rem',
                            fontWeight: 500,
                        }
                    }}
                >
                    <Tab icon={<Storage />} label="База данных" iconPosition="start" />
                    <Tab icon={<Email />} label="Email (SMTP)" iconPosition="start" />
                    <Tab icon={<Chat />} label="Mattermost" iconPosition="start" />
                    <Tab icon={<Business />} label="CRM интеграция" iconPosition="start" />
                    <Tab icon={<Business />} label="Tilda интеграция" iconPosition="start" />
                </Tabs>
            </Paper>

            <TabPanel value={tabValue} index={0}>
                <Fade in timeout={300}>
                    <Box>
                        <SettingCard
                            title="PostgreSQL Database"
                            icon={<Storage />}
                            onTest={() => testConnection('database')}
                            testStatus={testResults.database}
                        >
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={8}>
                                    <TextField
                                        fullWidth
                                        label="Хост"
                                        value={formData.database_host}
                                        onChange={handleInputChange('database_host')}
                                        placeholder="localhost"
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">postgresql://</InputAdornment>,
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="Порт"
                                        type="number"
                                        value={formData.database_port}
                                        onChange={handleInputChange('database_port')}
                                        placeholder="5432"
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Пользователь"
                                        value={formData.database_user}
                                        onChange={handleInputChange('database_user')}
                                        placeholder="postgres"
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Пароль"
                                        type={showPasswords.smtp ? 'text' : 'password'}
                                        value={formData.database_password}
                                        onChange={handleInputChange('database_password')}
                                        placeholder="••••••••"
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() => togglePasswordVisibility('smtp')}
                                                        edge="end"
                                                    >
                                                        {showPasswords.smtp ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={8}>
                                    <TextField
                                        fullWidth
                                        label="Имя базы данных"
                                        value={formData.database_name}
                                        onChange={handleInputChange('database_name')}
                                        placeholder="applications_db"
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        select
                                        label="SSL режим"
                                        value={formData.database_ssl_mode}
                                        onChange={handleInputChange('database_ssl_mode')}
                                    >
                                        <MenuItem value="disable">Отключен</MenuItem>
                                        <MenuItem value="require">Требуется</MenuItem>
                                        <MenuItem value="verify-ca">Проверка CA</MenuItem>
                                        <MenuItem value="verify-full">Полная проверка</MenuItem>
                                    </TextField>
                                </Grid>
                            </Grid>
                        </SettingCard>
                    </Box>
                </Fade>
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
                <Fade in timeout={300}>
                    <Box>
                        <SettingCard
                            title="Email (SMTP) настройки"
                            icon={<Email />}
                            enabled={formData.smtp_enabled}
                            onTest={() => testConnection('smtp')}
                            testStatus={testResults.smtp}
                        >
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.smtp_enabled}
                                        onChange={handleInputChange('smtp_enabled')}
                                    />
                                }
                                label="Включить отправку email"
                                sx={{ mb: 2 }}
                            />

                            <Grid container spacing={2}>
                                <Grid item xs={12} md={8}>
                                    <TextField
                                        fullWidth
                                        label="SMTP сервер"
                                        value={formData.smtp_host}
                                        onChange={handleInputChange('smtp_host')}
                                        placeholder="smtp.gmail.com"
                                        disabled={!formData.smtp_enabled}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="SMTP порт"
                                        type="number"
                                        value={formData.smtp_port}
                                        onChange={handleInputChange('smtp_port')}
                                        placeholder="587"
                                        disabled={!formData.smtp_enabled}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="SMTP пользователь"
                                        value={formData.smtp_username}
                                        onChange={handleInputChange('smtp_username')}
                                        placeholder="your-email@gmail.com"
                                        disabled={!formData.smtp_enabled}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="SMTP пароль"
                                        type={showPasswords.smtp ? 'text' : 'password'}
                                        value={formData.smtp_password}
                                        onChange={handleInputChange('smtp_password')}
                                        placeholder="••••••••"
                                        disabled={!formData.smtp_enabled}
                                        helperText="Для Gmail используйте пароль приложения"
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() => togglePasswordVisibility('smtp')}
                                                        edge="end"
                                                    >
                                                        {showPasswords.smtp ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Адрес отправителя"
                                        value={formData.smtp_from}
                                        onChange={handleInputChange('smtp_from')}
                                        placeholder="noreply@yourcompany.com"
                                        disabled={!formData.smtp_enabled}
                                    />
                                </Grid>
                            </Grid>

                            <Alert severity="info" sx={{ mt: 2 }}>
                                <Typography variant="body2">
                                    <strong>Для Gmail:</strong> Включите двухфакторную аутентификацию и создайте пароль приложения на{' '}
                                    <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer">
                                        myaccount.google.com/apppasswords
                                    </a>
                                </Typography>
                            </Alert>
                        </SettingCard>
                    </Box>
                </Fade>
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
                <Fade in timeout={300}>
                    <Box>
                        <SettingCard
                            title="Mattermost интеграция"
                            icon={<Chat />}
                            enabled={formData.mattermost_enabled}
                            onTest={() => testConnection('mattermost')}
                            testStatus={testResults.mattermost}
                        >
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.mattermost_enabled}
                                        onChange={handleInputChange('mattermost_enabled')}
                                    />
                                }
                                label="Включить интеграцию с Mattermost"
                                sx={{ mb: 2 }}
                            />

                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="URL Mattermost"
                                        value={formData.mattermost_url}
                                        onChange={handleInputChange('mattermost_url')}
                                        placeholder="https://your-mattermost.com"
                                        disabled={!formData.mattermost_enabled}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Webhook токен"
                                        type={showPasswords.mattermost ? 'text' : 'password'}
                                        value={formData.mattermost_token}
                                        onChange={handleInputChange('mattermost_token')}
                                        placeholder="••••••••"
                                        disabled={!formData.mattermost_enabled}
                                        helperText="Токен из Incoming Webhook"
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() => togglePasswordVisibility('mattermost')}
                                                        edge="end"
                                                    >
                                                        {showPasswords.mattermost ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Канал"
                                        value={formData.mattermost_channel}
                                        onChange={handleInputChange('mattermost_channel')}
                                        placeholder="general"
                                        disabled={!formData.mattermost_enabled}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">#</InputAdornment>,
                                        }}
                                    />
                                </Grid>
                            </Grid>

                            <Alert severity="info" sx={{ mt: 2 }}>
                                <Typography variant="body2">
                                    Создайте Incoming Webhook в настройках Mattermost и используйте полученный токен
                                </Typography>
                            </Alert>
                        </SettingCard>
                    </Box>
                </Fade>
            </TabPanel>

            <TabPanel value={tabValue} index={3}>
                <Fade in timeout={300}>
                    <Box>
                        <SettingCard
                            title="CRM интеграция"
                            icon={<Business />}
                            enabled={formData.crm_enabled}
                            onTest={() => testConnection('crm')}
                            testStatus={testResults.crm}
                        >
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.crm_enabled}
                                        onChange={handleInputChange('crm_enabled')}
                                    />
                                }
                                label="Включить интеграцию с CRM"
                                sx={{ mb: 2 }}
                            />

                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="URL CRM"
                                        value={formData.bpm_url}
                                        onChange={handleInputChange('bpm_url')}
                                        placeholder="https://your-crm.com"
                                        disabled={!formData.crm_enabled}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Логин"
                                        value={formData.bpm_login}
                                        onChange={handleInputChange('bpm_login')}
                                        placeholder="login"
                                        disabled={!formData.crm_enabled}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Пароль"
                                        type={showPasswords.crm ? 'text' : 'password'}
                                        value={formData.bpm_password}
                                        onChange={handleInputChange('bpm_password')}
                                        placeholder="••••••••"
                                        disabled={!formData.crm_enabled}
                                        helperText="Пароль для авторизации в CRM системе"
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() => togglePasswordVisibility('crm')}
                                                        edge="end"
                                                    >
                                                        {showPasswords.crm ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                            </Grid>

                            <Alert severity="info" sx={{ mt: 2 }}>
                                <Typography variant="body2">
                                    Заявки будут автоматически передаваться в вашу CRM систему
                                </Typography>
                            </Alert>
                        </SettingCard>
                    </Box>
                </Fade>
            </TabPanel>

            <TabPanel value={tabValue} index={4}>
                <Fade in timeout={300}>
                    <Box>
                        <SettingCard
                            title="Tilda интеграция"
                            icon={<Business />}
                            enabled={formData.tilda_enabled}
                            onTest={() => testConnection('tilda')}
                            testStatus={testResults.tilda}
                        >
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.tilda_enabled}
                                        onChange={handleInputChange('tilda_enabled')}
                                    />
                                }
                                label="Включить интеграцию с Tilda"
                                sx={{ mb: 2 }}
                            />

                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="URL Tilda"
                                        value={formData.url}
                                        onChange={handleInputChange('url')}
                                        placeholder="https://api.tildacdn.info"
                                        disabled={!formData.tilda_enabled}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="ID проекта"
                                        value={formData.project_id}
                                        onChange={handleInputChange('project_id')}
                                        placeholder="login"
                                        disabled={!formData.tilda_enabled}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Публичный ключ"
                                        type={showPasswords.tilda ? 'text' : 'password'}
                                        value={formData.public_key}
                                        onChange={handleInputChange('public_key')}
                                        placeholder="••••••••"
                                        disabled={!formData.crm_enabled}
                                        helperText="Публичный ключ для доступа к API Tilda"
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() => togglePasswordVisibility('tilda')}
                                                        edge="end"
                                                    >
                                                        {showPasswords.tilda ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Секретный ключ"
                                        type={showPasswords.tilda ? 'text' : 'password'}
                                        value={formData.secret_key}
                                        onChange={handleInputChange('secret_key')}
                                        placeholder="••••••••"
                                        disabled={!formData.crm_enabled}
                                        helperText="Секретный ключ для доступа к API Tilda"
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() => togglePasswordVisibility('tilda')}
                                                        edge="end"
                                                    >
                                                        {showPasswords.tilda ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                            </Grid>

                            <Alert severity="info" sx={{ mt: 2 }}>
                                <Typography variant="body2">
                                    Данные подключения к Tilda для получения информации по формам со страниц проекта
                                </Typography>
                            </Alert>
                        </SettingCard>
                    </Box>
                </Fade>
            </TabPanel>

            {/* Кнопки управления */}
            <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                    variant="outlined"
                    size="large"
                    onClick={() => settingsStore.fetchSettings()}
                >
                    Отменить
                </Button>
                <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={settingsStore.saving}
                    size="large"
                    sx={{
                        minWidth: 200,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        '&:hover': {
                            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                        }
                    }}
                >
                    {settingsStore.saving ? (
                        <>
                            <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                            Сохранение...
                        </>
                    ) : (
                        'Сохранить настройки'
                    )}
                </Button>
            </Box>

            {/* Информационная панель */}
            <Paper
                sx={{
                    p: 3,
                    mt: 4,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.info.main, 0.05)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                }}
            >
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Info color="info" />
                    <Typography variant="h6" fontWeight={700}>
                        Важная информация
                    </Typography>
                </Box>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="body2" paragraph>
                            <strong>Применение настроек:</strong> Изменения в настройках базы данных требуют перезапуска приложения. Остальные настройки применяются мгновенно.
                        </Typography>
                        <Typography variant="body2">
                            <strong>Безопасность:</strong> Пароли и ключи хранятся в зашифрованном виде. Оставьте поля паролей пустыми, чтобы не изменять их.
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Typography variant="body2" paragraph>
                            <strong>Тестирование:</strong> Используйте кнопку тестирования для проверки корректности настроек перед сохранением.
                        </Typography>
                        <Typography variant="body2">
                            <strong>Резервное копирование:</strong> Рекомендуется создать резервную копию настроек перед внесением изменений.
                        </Typography>
                    </Grid>
                </Grid>
            </Paper>
        </Container>
    );
});

export default Settings;